import { EOrderDeliveryStatus, IOrderReadResourceV18 } from 'components/order/model/Order';
import React from 'react';
import moment from 'moment';
import { getOrderDeliveryStatusString, isDeliveryOrderActive } from '../helpers';
import { useTranslation } from 'react-i18next';

const DELIVERY_POLLING_TIMEOUT = 30000;

export function useDeliverySummary(order: IOrderReadResourceV18 | null, pollOrder: () => void) {
    const [updatedAt, setUpdatedAt] = React.useState<Date>();
    const { t } = useTranslation();
    const statusString = React.useMemo(() => getOrderDeliveryStatusString(order), [order]);
    const due = React.useMemo(
        () =>
            t('DELIVERY_STATUS_EXPECTED', {
                time: `${moment(order?.delivery?.eta?.dropoff).format('h:mm a')} - ${moment(
                    order?.delivery?.eta?.dropoffDeadline
                ).format('h:mm a')}`
            }),
        [order?.delivery?.eta?.dropoff, order?.delivery?.eta?.dropoffDeadline, t]
    );
    const pin = React.useMemo(() => order?.delivery?.pin, [order?.delivery?.pin]);
    const stage = React.useMemo<0 | 1 | 2 | 3 | 4 | 5 | undefined>(() => {
        switch (order?.delivery?.status) {
            case EOrderDeliveryStatus.PENDING:
            case EOrderDeliveryStatus.LOOKING_FOR_DRIVER:
                return 0;
            case EOrderDeliveryStatus.DRIVER_EN_ROUTE_TO_PICKUP:
                return 1;
            case EOrderDeliveryStatus.DRIVER_AT_PICKUP:
                return 2;
            case EOrderDeliveryStatus.IN_TRANSIT:
                return 3;
            case EOrderDeliveryStatus.DRIVER_AT_DROPOFF:
                return 4;
            case EOrderDeliveryStatus.SUCCESS:
                return 5;
            case EOrderDeliveryStatus.FAILED:
            case EOrderDeliveryStatus.CANCELLED:
            default:
                return undefined;
        }
    }, [order?.delivery?.status]);
    const showPin = React.useMemo(
        () => !!order?.delivery?.pin && order?.delivery?.inProgress,
        [order?.delivery?.inProgress, order?.delivery?.pin]
    );
    const showDelivery = React.useMemo(() => !!order?.delivery, [order?.delivery]);
    const trackMyOrderLink = React.useMemo(
        () =>
            (order && showDelivery && isDeliveryOrderActive(order) && order?.delivery?.trackingUrl) ||
            undefined,
        [order, showDelivery]
    );
    React.useEffect(() => {
        if (order) {
            setUpdatedAt(order?.delivery?.inProgress ? new Date() : order?.delivery?.lastStatusAt);
        }
    }, [order]);
    React.useEffect(() => {
        if (order && order.delivery?.inProgress) {
            const timeout = setTimeout(() => {
                pollOrder();
            }, DELIVERY_POLLING_TIMEOUT);
            return () => clearTimeout(timeout);
        }
    }, [order, order?.delivery?.inProgress, pollOrder]);
    return {
        showDelivery,
        due,
        pin,
        showPin,
        updatedAt,
        statusString,
        trackMyOrderLink,
        deliveryStage: stage
    };
}
